// Pure, framework-free helpers for the playbook Create/Edit form. Kept out of the React
// component so the JSON validate / parse / format / transform logic is unit-testable in
// isolation (REF-01 s.1.2 field shapes; sub-plan 15 endpoints consume parsed JSON objects).

// The 7 root-cause categories (REF-07 s.3 — single source). null/empty is also allowed for
// hand-authored playbooks (REF-01 s.1.2 root_cause_category is nullable).
export const ROOT_CAUSE_CATEGORIES = [
  'software_bug',
  'hardware_fault',
  'firmware_issue',
  'config',
  'user_error',
  'network',
  'unknown',
] as const;

export type RootCauseCategory = (typeof ROOT_CAUSE_CATEGORIES)[number];

// The 12 diagnostic tool names (REF-02 s.3 / sub-plan 10). Surfaced as a hint so a hand-authored
// diagnostic step's `tool` matches the backend's strict enum.
export const DIAGNOSTIC_TOOL_NAMES = [
  'resolve_anchor',
  'get_session',
  'get_transaction',
  'get_charger',
  'get_site',
  'get_user_context',
  'get_charger_active_sessions',
  'get_reservation',
  'get_charger_logs',
  'find_status_notifications',
  'get_session_logs',
  'search_logs',
] as const;

// The read-only fields that must NEVER be in the form payload (display only). The transform
// strips them so a client cannot rewrite provenance/timestamps.
export const READ_ONLY_FIELDS = [
  'createdFromInvestigationId',
  'createdBy',
  'createdAt',
  'updatedAt',
] as const;

// A backend-valid diagnostic step example shown in the field hint so the operator types a
// step that passes sub-plan 15's strict { tool, params_template, check, on_match } schema.
export const DIAGNOSTIC_STEPS_EXAMPLE = JSON.stringify(
  [
    {
      tool: 'get_charger',
      params_template: { charger_id: '{{charger_id}}' },
      check: 'Inspect the charger status and error code.',
      on_match: 'Supports the diagnosed root cause.',
    },
  ],
  null,
  2,
);

// Pretty-print a JSON value (object/array) into the string the monospace TextInput displays.
// Used as the `format` of the JSON inputs. null/undefined → '' (empty field, not the string "null").
export const formatJson = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value; // already a string (e.g. mid-edit) — show as-is
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
};

// The `parse` of the JSON inputs keeps the raw STRING in the form state (so the user keeps typing
// freely and `validate` can run on the string). The string→object conversion happens in `transform`
// at submit time, never silently here.
export const parseJsonInput = (value: string): string => value;

// Returns true when `s` is blank (treated as "field omitted").
const isBlank = (s: unknown): boolean =>
  s === null || s === undefined || (typeof s === 'string' && s.trim() === '');

// react-admin field validator: the string must parse as JSON. Returns the i18n error KEY on failure
// (react-admin translates the returned key), or undefined when valid/blank. It does NOT enforce the
// inner object shape — the backend's strict schema does that (surfaced via the dataProvider error).
export const validateJsonString = (value: unknown): string | undefined => {
  if (isBlank(value)) return undefined; // optional field; blank is allowed
  try {
    JSON.parse(value as string);
    return undefined;
  } catch {
    return 'resources.ai-playbooks.errors.invalidJson';
  }
};

// JSON-parse the two JSON string inputs back into real objects/arrays and strip the read-only
// fields, so the dataProvider (and the backend) receive real JSON, never a stringified blob.
// `diagnosticSteps` defaults to [] when blank; `triggerConditions` defaults to null when blank
// (do NOT coerce invalid JSON to {}/[] — the validator blocks submit before this runs).
export const transformPlaybook = <T extends Record<string, any>>(data: T): Record<string, any> => {
  const out: Record<string, any> = { ...data };

  for (const f of READ_ONLY_FIELDS) {
    delete out[f];
  }

  // triggerConditions: blank → null; otherwise parse the string to an object.
  if (isBlank(out.triggerConditions)) {
    out.triggerConditions = null;
  } else if (typeof out.triggerConditions === 'string') {
    out.triggerConditions = JSON.parse(out.triggerConditions);
  }

  // diagnosticSteps: blank → []; otherwise parse the string to an array.
  if (isBlank(out.diagnosticSteps)) {
    out.diagnosticSteps = [];
  } else if (typeof out.diagnosticSteps === 'string') {
    out.diagnosticSteps = JSON.parse(out.diagnosticSteps);
  }

  return out;
};
